import { WebAnalyserProbe } from "@/modules/audio-recording/legacy";
import type { AudioLevelProbe } from "@/modules/audio-recording/legacy";
import type {
  MediaVisualizerEngine,
  MediaVisualizerStartOptions,
  MediaVisualizerStartResult,
} from "./types";

export class WebMediaVisualizer implements MediaVisualizerEngine {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private visualizer: AudioLevelProbe | null = null;
  private currentStreamId: string | null = null;

  async start(options: MediaVisualizerStartOptions): Promise<MediaVisualizerStartResult> {
    const { stream, sampleRate } = options;
    if (!stream) {
      throw new Error("Media stream is required for web visualizer.");
    }
    const canReuseExisting =
      this.audioContext && this.analyser && this.visualizer && this.currentStreamId === stream.id;

    if (canReuseExisting) {
      return {
        audioContext: this.audioContext!,
        analyser: this.analyser!,
        visualizer: this.visualizer!,
      };
    }

    await this.stop();

    const audioContext = new AudioContext({
      sampleRate,
      latencyHint: "interactive",
    });
    const analyser = audioContext.createAnalyser();
    const sourceNode = audioContext.createMediaStreamSource(stream);
    sourceNode.connect(analyser);
    const visualizer = new WebAnalyserProbe(analyser);

    this.audioContext = audioContext;
    this.analyser = analyser;
    this.sourceNode = sourceNode;
    this.visualizer = visualizer;
    this.currentStreamId = stream.id;

    return {
      audioContext,
      analyser,
      visualizer,
    };
  }

  async stop(): Promise<void> {
    if (this.visualizer) {
      this.visualizer.dispose();
      this.visualizer = null;
    }

    if (this.sourceNode) {
      try {
        this.sourceNode.disconnect();
      } catch {
        // ignore disconnect errors
      } finally {
        this.sourceNode = null;
      }
    }

    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch {
        // ignore disconnect errors
      } finally {
        this.analyser = null;
      }
    }

    if (this.audioContext) {
      await this.audioContext.close().catch(() => undefined);
      this.audioContext = null;
    }

    this.currentStreamId = null;
  }

  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  getAnalyser(): AnalyserNode | null {
    return this.analyser;
  }

  getVisualizer(): AudioLevelProbe | null {
    return this.visualizer;
  }
}
